import {
  AlertTriangle,
  ArrowRightLeft,
  Ban,
  CheckCircle2,
  Circle,
  ClipboardCheck,
  ClipboardList,
  Gift,
  Globe2,
  type LucideIcon,
  Mail,
  MapPin,
  Pencil,
  Phone,
  PlusCircle,
  RefreshCw,
  ShieldCheck,
  ShieldOff,
  StickyNote,
  UserPlus,
} from 'lucide-react';

import type { Activity_Types_Enum_Enum } from '@graphql/__generated__/graphql';
import type { ActivityType } from '@modules/_shared/components/feed/types';

export const ACTIVITY_ICONS: Record<
  | Activity_Types_Enum_Enum
  | 'appraisalRequest'
  | 'prospectionMapContact'
  | 'changePortal'
  | 'changeDeactivation'
  | 'changeRejection'
  | 'changeAutoReviewHeld'
  | 'changeAutoReviewApproved'
  | 'changeFsboBlock'
  | 'changeFsboUnblock',
  LucideIcon
> = {
  assignment: Gift,
  appraisalRequest: UserPlus,
  prospectionMapContact: Mail,
  requalification: RefreshCw,
  note: StickyNote,
  origination: PlusCircle,
  visit: MapPin,
  task: ClipboardList,
  call: Phone,
  email: Mail,
  change: Pencil,
  lead_status_agent: ArrowRightLeft,
  changePortal: Globe2,
  // FSBO deactivation (status → paused with fsbo context) and rejection
  // (status → rejected with fsbo context) are notable lifecycle events; a
  // warning triangle reads at a glance vs. the generic edit pencil.
  changeDeactivation: AlertTriangle,
  changeRejection: AlertTriangle,
  // Auto-review: a held listing gets a warning triangle (toned orange by the
  // caller), an auto-approved one a success check.
  changeAutoReviewHeld: AlertTriangle,
  changeAutoReviewApproved: CheckCircle2,
  // block_fsbo_user: the advertiser was shadow banned (toned red by the
  // caller).
  changeFsboBlock: Ban,
  // unblock_fsbo_users: the account was cleared (toned green by the caller).
  changeFsboUnblock: CheckCircle2,
  create_user: UserPlus,
  match: Circle,
  offer: Circle,
  agent_opt_in: PlusCircle,
  agent_quiz_completed: ClipboardCheck,
  module_granted: ShieldCheck,
  module_revoked: ShieldOff,
  onboarding_status_changed: ClipboardCheck,
  agent_profile_visibility_changed: Globe2,
  // Never rendered (FEED_HIDDEN_ACTIVITY_TYPES).
  onboarding_enrolled: Circle,
  onboarding_completed: Circle,
  agency_profile_visibility_changed: Circle,
} as const;

/**
 * Onboarding rows written by database triggers that tenant admins can read
 * but the feed does not show: enrolment and completion repeat the status
 * change written with them, and an agency publication has no user to show.
 * Status changes and agent publications are shown, like module grants.
 */
export const FEED_HIDDEN_ACTIVITY_TYPES: ReadonlySet<string> = new Set([
  'onboarding_enrolled',
  'onboarding_completed',
  'agency_profile_visibility_changed',
]);

/** Activity types that support outcome actions (completed/cancelled) */
export type OutcomeActivityType = 'call' | 'visit' | 'task';

const OUTCOME_ACTIVITY_TYPES: readonly OutcomeActivityType[] = [
  'call',
  'visit',
  'task',
];

/** Type guard to check if activity type supports outcomes */
export const isOutcomeActivityType = (
  type: string,
): type is OutcomeActivityType => {
  return (OUTCOME_ACTIVITY_TYPES as readonly string[]).includes(type);
};

/** Type guard to check if activity type is editable */
export const isEditableActivityType = (type: string): type is ActivityType => {
  return (EDITABLE_TYPES as readonly string[]).includes(type);
};

// Matches EditableTypes in FeedItem.tsx (uses string values instead of enum)
export const EDITABLE_TYPES = ['call', 'visit', 'task', 'note'];
